import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { selectableEventTypes, eventTypesByCategory } from "../../eventTypes/index.js";
import { validateSlug } from "../../lib/slug.js";
import { reserveSlug, checkSlugAvailable } from "../../firebase/db.js";
import { useAuth } from "../auth/AuthContext.jsx";
import { sendMagicLink } from "../../firebase/auth.js";

// Claim-then-sign-in:
// 1) choose event type -> reserveSlug() (server creates draft + token)
// 2) if not signed in, send magic link with ?next=/app/claim/<slug>/finish
//    holding the claimToken so finalizeClaim can run after sign-in.
export default function ClaimFlow() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, configured } = useAuth();

  const [availability, setAvailability] = useState({ checking: true });
  const [type, setType] = useState(selectableEventTypes[0]?.id);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [sent, setSent] = useState(false);

  const slugCheck = validateSlug(slug);

  useEffect(() => {
    if (!slugCheck.ok) { setAvailability({ checking: false, available: false, reason: slugCheck.reason }); return; }
    if (!configured) { setAvailability({ checking: false, available: true, preview: true }); return; }
    checkSlugAvailable(slug)
      .then((r) => setAvailability({ checking: false, ...r }))
      .catch((e) => setAvailability({ checking: false, available: false, reason: e.message }));
  }, [slug, configured]);

  async function claim() {
    setErr(null);
    setBusy(true);
    try {
      const { claimToken } = await reserveSlug(slug, type);
      // Stash token so finalize can run after auth round-trip.
      sessionStorage.setItem(`einvite:token:${slug}`, claimToken);

      if (user) {
        navigate(`/app/claim/${slug}/finish`);
      } else {
        await sendMagicLink(email, `/app/claim/${slug}/finish`);
        setSent(true);
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (availability.checking) return <Centered>Checking availability…</Centered>;
  if (!availability.available) {
    return (
      <Centered>
        <h2>“{slug}” isn't available</h2>
        <p className="muted">{availability.reason || "That link is taken."}</p>
        <button className="btn" onClick={() => navigate("/")}>Try another</button>
      </Centered>
    );
  }

  if (sent) {
    return (
      <Centered>
        <h2>Check your inbox ✉️</h2>
        <p className="muted">
          We sent a sign-in link to <strong>{email}</strong>. Open it on this device
          to finish claiming <strong>{slug}</strong>.
        </p>
      </Centered>
    );
  }

  return (
    <div className="container" style={{ paddingTop: 48, paddingBottom: 80, maxWidth: 760 }}>
      <p className="muted">You're claiming</p>
      <h1 style={{ marginTop: 4 }}>einvite.to/{slug}</h1>

      <h3 style={{ marginTop: 32 }}>What kind of event is it?</h3>
      {eventTypesByCategory().map((group) => (
        <div key={group.id} className="stack" style={{ gap: 12, marginTop: 20 }}>
          <div>
            <h4 style={{ margin: 0 }}>{group.label}</h4>
            {group.blurb && <p className="muted" style={{ margin: "2px 0 0", fontSize: ".9rem" }}>{group.blurb}</p>}
          </div>
          <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))" }}>
            {group.types.map((t) => {
              const disabled = t.enabled === false;
              const active = type === t.id;
              return (
                <button
                  key={t.id}
                  className={`card claim-type-card${active ? " active" : ""}`}
                  disabled={disabled}
                  onClick={() => setType(t.id)}
                  style={{
                    opacity: disabled ? 0.5 : 1,
                  }}
                >
                  <div style={{ fontSize: 28 }}>{t.icon}</div>
                  <div className="claim-type-title">
                    {t.label} {disabled && <span className="muted">(soon)</span>}
                  </div>
                  <div className="claim-type-blurb">{t.blurb}</div>
                </button>
              );
            })}
          </div>
        </div>
      ))}

      {!user && (
        <div className="stack" style={{ marginTop: 32 }}>
          <h3>Where should we send your sign-in link?</h3>
          <input
            className="input"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <p className="muted" style={{ fontSize: ".9rem" }}>
            We use a passwordless email link — no password to remember.
          </p>
        </div>
      )}

      {err && <p className="error" style={{ marginTop: 16 }}>{err}</p>}

      <button
        className="btn"
        style={{ marginTop: 24 }}
        disabled={busy || (!user && !email)}
        onClick={claim}
      >
        {busy ? "Working…" : user ? "Claim & start" : "Claim & send link"}
      </button>
    </div>
  );
}

function Centered({ children }) {
  return (
    <div className="container center stack" style={{ paddingTop: 96, maxWidth: 560 }}>
      {children}
    </div>
  );
}
